import React, { useState, useCallback, useMemo, useEffect } from "react";
import "../css/home.css";
import { useI18n } from "../v2/i18n/I18nProvider";
import {
  capturarAtribucion,
  payloadAtribucion,
  enviarLead,
  pixel,
  eventIdPara,
  refCode,
} from "../utils/attribution";

/* ═══════════════════════════════════════════════════════════════
   LANDING "EL CIERRE"
   ───────────────────────────────────────────────────────────────
   Sistema visual: cinta de papel térmico. Todo el copy vive en
   src/v2/i18n/landing.js bajo el prefijo `landing.` (las claves
   `home.*` viejas quedaron sin uso).

   Las revelaciones al scrollear y la comanda que se "imprime"
   renglón por renglón son CSS puro (animation-timeline: view()),
   no JS: por eso esta pantalla no necesita framer-motion. El
   estado por defecto es VISIBLE, así que en un navegador sin
   scroll-driven animations se ve todo igual, sin animación.
   ═══════════════════════════════════════════════════════════════ */

const WA_NUMERO = "5491125452554";
const WA_VISIBLE = "11 2545-2554";
const MAIL = "cartadigitalweb@gmail.com";
const GOOGLE_RESENAS = "https://maps.app.goo.gl/k4FyqMckzQvacg2M6";
const FORMSPREE = "https://formspree.io/f/xjvqqvrq";

const PRECIO_CARTA = 17000;
const PRECIO_FULL = 27000;

/* Clientes reales, escritos como figuran en src/data/clients.json. */
const CLIENTES = [
  "Felichea", "Ches", "Eden Sushi", "Hotel La María", "The Row",
  "Mauro It", "Waffles Lovers", "Burger House", "A Fuego Lento",
];

/* ── helpers ── */

/** Fallback si Intl no está disponible: separador de miles con punto. */
const agruparEs = (n) =>
  Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/** Renderiza `**negrita**` sin meter HTML en las traducciones. */
function Rich({ text }) {
  const partes = String(text ?? "").split("**");
  return (
    <>
      {partes.map((p, i) =>
        i % 2 ? <strong key={i}>{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

function Estrellas({ size = 17 }) {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l3 6.5 7 .9-5 4.9 1.2 7L12 18l-6.2 3.3L7 14.3l-5-4.9 7-.9z" />
        </svg>
      ))}
    </>
  );
}

function IconoWhatsapp({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.5 14.4c-.3-.2-1.8-.9-2-1-.3-.1-.5-.2-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-1.7-.9-2.9-1.6-4-3.6-.3-.5.3-.5.8-1.6.1-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5 1.9.8 2.6.9 3.5.7.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.2-.3-.2-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.4 1.3 4.9L2 22l5.3-1.4c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
    </svg>
  );
}

/* ── secciones que son puro dato: se arman con map ── */

const RENGLONES = [
  { hora: "18:40", quien: "who1", k: "r1" },
  { hora: "20:05", quien: "who1", k: "r2" },
  { hora: "20:40", quien: "who2", k: "r3" },
  { hora: "22:15", quien: "who2", k: "r4" },
  { hora: "23:50", quien: "who3", k: "r5" },
  { hora: "09:30", quien: "who1", k: "r6" },
];

const CADENA = ["c1", "c2", "c3", "c4", "c5"];
const MODULOS = [
  { k: "m1", pack: false }, { k: "m2", pack: false }, { k: "m3", pack: false },
  { k: "m4", pack: false }, { k: "m5", pack: true }, { k: "m6", pack: true },
  { k: "m7", pack: true },
];
const PASOS = ["s1", "s2", "s3"];
const DUDAS = ["1", "2", "3", "4", "5", "6"];

/* ═══════════════════════════════════════════════════════════════ */

const Home = () => {
  const { t, locale } = useI18n();

  /* Los miles cambian de separador según el idioma: 1.284.500 en es/pt, 1,284,500 en en. */
  const agrupar = useCallback((n) => {
    try { return new Intl.NumberFormat(locale).format(Math.round(n)); }
    catch { return agruparEs(n); }
  }, [locale]);
  const pesos = useCallback((n) => "$" + agrupar(n), [agrupar]);

  /* ── calculadora ── */
  const [mesas, setMesas] = useState(38);
  const [ticket, setTicket] = useState(33000);
  const [noches, setNoches] = useState(6);

  const cuenta = useMemo(() => {
    const mesasMes = mesas * noches * 4.33;
    const mensual = mesasMes * ticket;
    const pct = (costo) => {
      if (!mensual) return "—";
      const p = (costo / mensual) * 100;
      const d = p < 1 ? 2 : 1;
      try {
        return new Intl.NumberFormat(locale, { minimumFractionDigits: d, maximumFractionDigits: d }).format(p) + "%";
      } catch {
        return p.toFixed(d).replace(".", ",") + "%";
      }
    };
    const nMesas = Math.max(1, Math.round(PRECIO_FULL / ticket));
    return {
      mesasMes: agrupar(mesasMes),
      mensual: pesos(mensual),
      pctCarta: pct(PRECIO_CARTA),
      pctFull: pct(PRECIO_FULL),
      punch: nMesas === 1 ? t("landing.s7.punch1") : t("landing.s7.punchN", { n: nMesas }),
    };
  }, [mesas, ticket, noches, t, locale, agrupar, pesos]);

  /* ── atribución ──
   * De qué anuncio salió esta persona. Se captura al montar (los UTM y el `fbclid` viven en el
   * URL y desaparecen apenas navegan) y viaja con el lead hasta el backend. Ver utils/attribution.
   * El `ref` se genera UNA vez por visita: es el código corto que va en el mensaje de WhatsApp
   * para poder atar esa conversación al anuncio — hoy ese canal es completamente invisible. */
  useEffect(() => { capturarAtribucion(); }, []);
  // El código vive en sessionStorage, no en useState: así sobrevive a recargar la página. Con
  // useState se regeneraba en cada montaje y el mismo visitante contaba como varios leads.
  const ref = refCode();

  /* ── formulario corto ── */
  const [form, setForm] = useState({ name: "", business: "", phone: "" });
  const [envio, setEnvio] = useState({ enviando: false, ok: false, error: false });

  const onInput = useCallback((e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Si ya se había enviado, el cartel viejo no puede quedar colgado mientras retipea.
    setEnvio((s) => (s.ok || s.error ? { enviando: false, ok: false, error: false } : s));
  }, []);

  const completo = form.name.trim() && form.business.trim() && form.phone.trim();

  const enviar = useCallback(
    async (e) => {
      e.preventDefault();
      if (!completo || envio.enviando) return;
      setEnvio({ enviando: true, ok: false, error: false });

      // El MISMO id en el evento del navegador y en el del servidor → Meta los deduplica y no
      // cuenta la conversión dos veces. Ver `pixel()` en utils/attribution.
      const eventId = eventIdPara("form");
      pixel("Lead", { content_name: "landing-el-cierre", currency: "ARS", value: 0 }, eventId);
      // Nuestro registro (no bloquea el formulario: si falla, el lead igual llega por Formspree).
      enviarLead({
        canal: "form",
        eventId,
        nombre: form.name,
        negocio: form.business,
        telefono: form.phone,
        attr: payloadAtribucion(),
      });

      try {
        const res = await fetch(FORMSPREE, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ ...form, origen: "landing-el-cierre", ref }),
        });
        setEnvio({ enviando: false, ok: res.ok, error: !res.ok });
        if (res.ok) setForm({ name: "", business: "", phone: "" });
      } catch {
        setEnvio({ enviando: false, ok: false, error: true });
      }
    },
    [form, completo, envio.enviando, ref]
  );

  /* WhatsApp: el canal por donde entra la mayoría y el único que no dejaba NINGÚN rastro.
   * El código corto viaja en el mensaje, así el que atiende puede buscarlo en el admin y ver de
   * qué campaña salió. `sendBeacon` sobrevive a que el navegador se vaya a WhatsApp. */
  const waHref = `https://wa.me/${WA_NUMERO}?text=${encodeURIComponent(
    `${t("landing.wa.msg")} [${ref}]`
  )}`;

  const onWhatsApp = useCallback(() => {
    // Un id por VISITA, no por clic: hay tres botones de WhatsApp en la landing y el mismo
    // visitante toca más de uno. Con un id nuevo por clic, el navegador mandaba dos o tres
    // `Contact` que Meta no podía deduplicar entre sí y la conversión salía inflada.
    const eventId = eventIdPara("whatsapp");
    pixel("Contact", { content_name: "whatsapp" }, eventId);
    enviarLead({ canal: "whatsapp", eventId, attr: payloadAtribucion() });
  }, []);

  return (
    <div className="ra-home">
      <div className="ra-feed" />
      <aside className="ra-rail" aria-hidden="true">
        <span>RestaurAdmin · {t("landing.tk.close")}</span>
      </aside>

      {/* ══════════ NAV DE SECCIONES ══════════ */}
      <nav className="ra-secnav" aria-label={t("landing.nav.aria")}>
        <div className="ra-wrap ra-secnav-in">
          <a href="#noche">{t("landing.nav.noche")}</a>
          <a href="#sistema">{t("landing.nav.sistema")}</a>
          <a href="#cuenta">{t("landing.nav.cuenta")}</a>
          <a href="#precios">{t("landing.nav.precios")}</a>
          <a className="ra-btn ra-btn--am ra-btn--sm ra-secnav-cta" href="#contacto">
            {t("landing.nav.cta")}
          </a>
        </div>
      </nav>

      {/* ══════════ HERO ══════════ */}
      <section className="ra-hero" id="top">
        <div className="ra-wrap ra-hero-in">
          <div>
            <span className="ra-kick">{t("landing.hero.kicker")}</span>
            <h1>
              <span>{t("landing.hero.t1")}</span>{" "}
              <span>{t("landing.hero.t2")}</span>{" "}
              <span>{t("landing.hero.t3")}</span>{" "}
              <span><span className="ra-mk">{t("landing.hero.t4")}</span></span>
            </h1>
            <p className="ra-hero-sub"><Rich text={t("landing.hero.sub")} /></p>
            <div className="ra-hero-cta">
              <a className="ra-btn ra-btn--am" href="#contacto">
                {t("landing.hero.ctaPrimary")}
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </a>
              <a className="ra-btn ra-btn--line" href="#cuenta">{t("landing.hero.ctaSecondary")}</a>
            </div>
            <div className="ra-hero-under">
              <span><b>+200</b> {t("landing.hero.m1")}</span>
              <span><b>21</b> {t("landing.hero.m2")}</span>
              <span><b>5,0 ★</b> {t("landing.hero.m3")}</span>
              <span>{t("landing.hero.m4", { precio: pesos(PRECIO_CARTA) })}</span>
            </div>
          </div>

          <div className="ra-hero-stack">
            <div className="ra-tk ra-tk--rot">
              <div className="ra-tk-brand">RESTAURADMIN</div>
              <div className="ra-tk-sub">{t("landing.tk.close")}</div>
              <div className="ra-tk-rule" />
              <div className="ra-tk-row"><span>{t("landing.tk.mesas")}</span><b>38</b></div>
              <div className="ra-tk-row"><span>{t("landing.tk.cubiertos")}</span><b>112</b></div>
              <div className="ra-tk-row"><span>{t("landing.tk.ticket")}</span><b>$33.802</b></div>
              <div className="ra-tk-rule" />
              <div className="ra-tk-row ra-tk-row--dim"><span>{t("landing.tk.efectivo")}</span><span>$412.000</span></div>
              <div className="ra-tk-row ra-tk-row--dim"><span>{t("landing.tk.debito")}</span><span>$338.500</span></div>
              <div className="ra-tk-row ra-tk-row--dim"><span>{t("landing.tk.credito")}</span><span>$301.000</span></div>
              <div className="ra-tk-row ra-tk-row--dim"><span>{t("landing.tk.transferencia")}</span><span>$233.000</span></div>
              <div className="ra-tk-rule" />
              <div className="ra-tk-row"><span className="ra-tk-big">{t("landing.tk.total")}</span><span className="ra-tk-big">$1.284.500</span></div>
              <div className="ra-tk-row"><span>{t("landing.tk.dif")}</span><b>$0</b></div>
              <div className="ra-tk-stamp">{t("landing.tk.cuadra")}</div>
              <div className="ra-tk-rule" />
              <div className="ra-tk-row ra-tk-row--dim"><span>{t("landing.tk.propina")}</span><span>$54.200</span></div>
              <div className="ra-tk-row ra-tk-row--dim"><span>{t("landing.tk.apps")}</span><span>$86.400</span></div>
              <div className="ra-tk-foot">{t("landing.tk.legal1")}<br />{t("landing.tk.legal2")}</div>
            </div>
            <div className="ra-tk-cap">{t("landing.tk.cap")}</div>
          </div>
        </div>
      </section>

      {/* ══════════ BAND ══════════ */}
      <div className="ra-band">
        <div className="ra-wrap ra-band-in">
          <div className="ra-band-i"><div className="ra-band-n">+<em>200</em></div><div className="ra-band-l">{t("landing.band.l1")}</div></div>
          <div className="ra-band-i"><div className="ra-band-n"><em>21</em></div><div className="ra-band-l">{t("landing.band.l2")}</div></div>
          <div className="ra-band-i"><div className="ra-band-n"><em>5</em>,0</div><div className="ra-band-l">{t("landing.band.l3")}</div></div>
          <div className="ra-band-i"><div className="ra-band-n"><em>0</em></div><div className="ra-band-l">{t("landing.band.l4")}</div></div>
        </div>
      </div>

      {/* ══════════ 01 · EL PROBLEMA ══════════ */}
      <section className="ra-sec" id="problema">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>01</b> {t("landing.s1.kicker")}</span>
            <h2>{t("landing.s1.h2")}</h2>
            <p className="ra-lead">{t("landing.s1.lead")}</p>
          </div>
          <div className="ra-dh ra-rev">
            <div className="ra-dh-col">
              <div className="ra-dh-h">{t("landing.s1.aHead")}</div>
              <div className="ra-dh-t">{t("landing.s1.aTitle")}</div>
              <ul className="ra-dh-list">
                {["a1", "a2", "a3", "a4", "a5"].map((k, i) => (
                  <li key={k}><i>{String(i + 1).padStart(2, "0")}</i><span>{t(`landing.s1.${k}`)}</span></li>
                ))}
              </ul>
              <div className="ra-dh-foot">→ {t("landing.s1.aFoot")}</div>
            </div>
            <div className="ra-dh-col ra-dh-col--after">
              <div className="ra-dh-h">{t("landing.s1.bHead")}</div>
              <div className="ra-dh-t">{t("landing.s1.bTitle")}</div>
              <ul className="ra-dh-list">
                {["b1", "b2", "b3", "b4", "b5"].map((k, i) => (
                  <li key={k}><i>{String(i + 1).padStart(2, "0")}</i><span>{t(`landing.s1.${k}`)}</span></li>
                ))}
              </ul>
              <div className="ra-dh-foot">→ {t("landing.s1.bFoot")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ 02 · LA NOCHE ══════════ */}
      <section className="ra-sec ra-sec--crema" id="noche">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>02</b> {t("landing.s2.kicker")}</span>
            <h2>{t("landing.s2.h2")}</h2>
            <p className="ra-lead">{t("landing.s2.lead")}</p>
          </div>
          <div className="ra-ledger">
            {RENGLONES.map((r) => (
              <div className="ra-ln ra-rev" key={r.k}>
                <div className="ra-ln-h">{r.hora}<em>{t(`landing.s2.${r.quien}`)}</em></div>
                <div className="ra-ln-b">
                  <h3>{t(`landing.s2.${r.k}t`)}</h3>
                  <p><Rich text={t(`landing.s2.${r.k}p`)} /></p>
                </div>
                <div className="ra-ln-s">
                  <span className="ra-ln-mod">{t(`landing.s2.${r.k}m`)}</span>
                  <span className="ra-ln-note">{t(`landing.s2.${r.k}n`)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ 03 · LA COMANDA ══════════ */}
      <section className="ra-sec ra-press" id="comanda">
        <div className="ra-wrap ra-press-in">
          <div className="ra-press-copy">
            <span className="ra-kick"><b>03</b> {t("landing.s3.kicker")}</span>
            <h2>{t("landing.s3.h2")}</h2>
            <p className="ra-lead"><Rich text={t("landing.s3.lead")} /></p>
            <div className="ra-keys">
              {["m", "i", "l", "a", "3"].map((k, i) => <span className="ra-key" key={i}>{k}</span>)}
              <span className="ra-key ra-key--hot">ESC · {t("landing.s3.key")}</span>
            </div>
          </div>

          <div className="ra-roll">
            <div className="ra-tk">
              <div className="ra-tk-brand ra-pl">{t("landing.s3.cmMesa")}</div>
              <div className="ra-tk-sub ra-pl">{t("landing.s3.cmSub")}</div>
              <div className="ra-tk-rule ra-pl" />
              <div className="ra-tk-row ra-pl"><span>{t("landing.s3.cmMozo")}</span><b>NICO</b></div>
              <div className="ra-tk-row ra-pl"><span>{t("landing.s3.cmCub")}</span><b>4</b></div>
              <div className="ra-tk-rule ra-pl" />
              <div className="ra-tk-row ra-pl"><b>2x MILANESA NAPOLITANA</b></div>
              <div className="ra-tk-row ra-pl ra-tk-row--dim"><span>&nbsp;&nbsp;&raquo;&raquo; {t("landing.s3.cmNota1")}</span></div>
              <div className="ra-tk-row ra-pl"><b>1x RABAS</b></div>
              <div className="ra-tk-row ra-pl"><b>3x PAPAS BRAVAS</b></div>
              <div className="ra-tk-row ra-pl ra-tk-row--dim"><span>&nbsp;&nbsp;&raquo;&raquo; {t("landing.s3.cmNota2")}</span></div>
              <div className="ra-tk-rule ra-pl" />
              <div className="ra-tk-row ra-pl"><b>2x AGUA CON GAS</b></div>
              <div className="ra-tk-row ra-pl"><b>1x MALBEC DE LA CASA</b></div>
              <div className="ra-tk-rule ra-pl" />
              <div className="ra-tk-row ra-pl ra-tk-row--dim"><span>{t("landing.s3.cmEnviado")}</span><span>21:14:06</span></div>
              <div className="ra-tk-row ra-pl ra-tk-row--dim"><span>{t("landing.s3.cmImpresora")}</span><span>COCINA-01</span></div>
              <div className="ra-tk-stamp ra-pl">{t("landing.s3.cmStamp")}</div>
              <div className="ra-tk-foot ra-pl">{t("landing.s3.cmFoot")}<span className="ra-caret" /></div>
            </div>
          </div>

          <div className="ra-press-pts">
            {["p1", "p2", "p3", "p4"].map((k, i) => (
              <div className="ra-press-pt" key={k}>
                <i>{String(i + 1).padStart(2, "0")}</i>
                <div>
                  <b>{t(`landing.s3.${k}t`)}</b>
                  <span>{t(`landing.s3.${k}d`)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ 04 · LA CADENA ══════════ */}
      <section className="ra-sec">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>04</b> {t("landing.s4.kicker")}</span>
            <h2>{t("landing.s4.h2")}</h2>
            <p className="ra-lead"><Rich text={t("landing.s4.lead")} /></p>
          </div>
          <div className="ra-chain ra-rev" tabIndex={0} role="group" aria-label={t("landing.s4.h2")}>
            {CADENA.map((k, i) => (
              <div className={`ra-ch${i === 0 ? " ra-ch--src" : ""}`} key={k}>
                <span className="ra-ch-k">
                  {String(i + 1).padStart(2, "0")}{i === 0 ? ` · ${t("landing.s4.origen")}` : ""}
                </span>
                <b>{t(`landing.s4.${k}t`)}</b>
                <span>{t(`landing.s4.${k}d`)}</span>
              </div>
            ))}
          </div>
          <div className="ra-swipe">← {t("landing.s4.swipe")}</div>
          <div className="ra-chain-foot ra-rev">{t("landing.s4.foot")}</div>
        </div>
      </section>

      {/* ══════════ 05 · MÓDULOS ══════════ */}
      <section className="ra-sec ra-sec--crema" id="sistema">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>05</b> {t("landing.s5.kicker")}</span>
            <h2>{t("landing.s5.h2")}</h2>
          </div>
          <table className="ra-mods ra-rev">
            <thead>
              <tr>
                <th style={{ width: 220 }}>{t("landing.s5.thMod")}</th>
                <th>{t("landing.s5.thQue")}</th>
                <th style={{ width: 140 }}>{t("landing.s5.thPlan")}</th>
              </tr>
            </thead>
            <tbody>
              {MODULOS.map((m) => (
                <tr key={m.k}>
                  <td className="ra-m-n">{t(`landing.s5.${m.k}n`)}</td>
                  <td className="ra-m-d">{t(`landing.s5.${m.k}d`)}</td>
                  <td>
                    <span className={`ra-m-p${m.pack ? " ra-m-p--pack" : ""}`}>
                      {t(m.pack ? "landing.s5.planPack" : "landing.s5.planCarta")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ══════════ 06 · PRUEBA ══════════ */}
      <section className="ra-sec ra-sec--tight" id="clientes">
        <div className="ra-wrap ra-proof">
          <div className="ra-proof-copy ra-rev">
            <span className="ra-kick"><b>06</b> {t("landing.s6.kicker")}</span>
            <h2>{t("landing.s6.h2")}</h2>
            <p className="ra-lead"><Rich text={t("landing.s6.lead")} /></p>
          </div>
          <a className="ra-gcard ra-rev" href={GOOGLE_RESENAS} target="_blank" rel="noopener noreferrer">
            <div className="ra-g-h">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 11v2.8h6.5c-.3 1.7-2 4.9-6.5 4.9-3.9 0-7.1-3.2-7.1-7.2S8.1 4.3 12 4.3c2.2 0 3.7.9 4.6 1.8l3.1-3C17.8 1.3 15.2 0 12 0 5.4 0 0 5.4 0 12s5.4 12 12 12c6.9 0 11.5-4.9 11.5-11.7 0-.8-.1-1.4-.2-2H12z" /></svg>
              {t("landing.s6.gTag")}
            </div>
            <div className="ra-g-n">5,0</div>
            <div className="ra-g-s"><Estrellas /></div>
            <div className="ra-g-t">{t("landing.s6.gNote")}</div>
            <span className="ra-g-l">{t("landing.s6.gLink")} →</span>
          </a>
          <div className="ra-names ra-rev">
            {CLIENTES.map((c) => <span className="ra-nm" key={c}>{c}</span>)}
            <span className="ra-nm ra-nm--more">{t("landing.s6.more")}</span>
          </div>
        </div>
      </section>

      {/* ══════════ 07 · LA CUENTA ══════════ */}
      <section className="ra-sec ra-sec--crema" id="cuenta">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>07</b> {t("landing.s7.kicker")}</span>
            <h2>{t("landing.s7.h2")}</h2>
            <p className="ra-lead"><Rich text={t("landing.s7.lead")} /></p>
          </div>

          <div className="ra-calc ra-rev">
            <div className="ra-calc-in">
              <div className="ra-calc-l">
                <h3>{t("landing.s7.calcTitle")}</h3>
                <p>{t("landing.s7.calcSub")}</p>

                <div className="ra-fld">
                  <div className="ra-fld-h"><span>{t("landing.s7.fMesas")}</span><b>{mesas}</b></div>
                  <input type="range" min="8" max="120" step="1" value={mesas}
                    onChange={(e) => setMesas(Number(e.target.value))}
                    aria-label={t("landing.s7.fMesas")} />
                  <div className="ra-fld-scale"><span>8</span><span>120</span></div>
                </div>
                <div className="ra-fld">
                  <div className="ra-fld-h"><span>{t("landing.s7.fTicket")}</span><b>{pesos(ticket)}</b></div>
                  <input type="range" min="6000" max="80000" step="500" value={ticket}
                    onChange={(e) => setTicket(Number(e.target.value))}
                    aria-label={t("landing.s7.fTicket")} />
                  <div className="ra-fld-scale"><span>$6.000</span><span>$80.000</span></div>
                </div>
                <div className="ra-fld">
                  <div className="ra-fld-h"><span>{t("landing.s7.fNoches")}</span><b>{noches}</b></div>
                  <input type="range" min="3" max="7" step="1" value={noches}
                    onChange={(e) => setNoches(Number(e.target.value))}
                    aria-label={t("landing.s7.fNoches")} />
                  <div className="ra-fld-scale"><span>3</span><span>7</span></div>
                </div>
              </div>

              <div className="ra-calc-r">
                <div className="ra-disp">
                  <div className="ra-disp-l">{t("landing.s7.dispLabel")}</div>
                  <div className="ra-disp-n">{cuenta.mensual}</div>
                  <div className="ra-disp-s">{t("landing.s7.dispSub", { mesas: cuenta.mesasMes })}</div>
                </div>
                <div className="ra-calc-rows">
                  <div className="ra-calc-row"><span>{t("landing.s8.p1k").replace(/^\d+\s·\s/, "")}</span><b>{pesos(PRECIO_CARTA)} · <em>{cuenta.pctCarta}</em></b></div>
                  <div className="ra-calc-row"><span>{t("landing.s8.p2k").replace(/^\d+\s·\s/, "")}</span><b>{pesos(PRECIO_FULL)} · <em>{cuenta.pctFull}</em></b></div>
                  <div className="ra-calc-row"><span>{t("landing.s7.rowInstal")}</span><b>$0</b></div>
                  <div className="ra-calc-row"><span>{t("landing.s7.rowPerm")}</span><b>{t("landing.s7.rowPermVal")}</b></div>
                </div>
                <div className="ra-punch">
                  <b>{cuenta.punch}</b>
                  <span>{t("landing.s7.punchSub")}</span>
                </div>
                <div className="ra-calc-fine">{t("landing.s7.fine")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ 08 · PRECIOS ══════════ */}
      <section className="ra-sec" id="precios">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>08</b> {t("landing.s8.kicker")}</span>
            <h2>{t("landing.s8.h2")}</h2>
            <p className="ra-lead">{t("landing.s8.lead")}</p>
          </div>
          <div className="ra-plans ra-rev">
            <div className="ra-plan">
              <span className="ra-p-k">{t("landing.s8.p1k")}</span>
              <h3>{t("landing.s8.p1t")}</h3>
              <div className="ra-who">{t("landing.s8.p1who")}</div>
              <div className="ra-pr"><b>{pesos(PRECIO_CARTA)}</b><span>{t("landing.s8.mes")}</span></div>
              <div className="ra-prn">{t("landing.s8.p1note")}</div>
              <ul>
                {["p1f1", "p1f2", "p1f3", "p1f4"].map((k) => (
                  <li key={k}><i>✓</i><span>{t(`landing.s8.${k}`)}</span></li>
                ))}
                <li className="ra-off"><i>✕</i><span>{t("landing.s8.p1f5")}</span></li>
              </ul>
              <a className="ra-btn ra-btn--line" href="#contacto">{t("landing.s8.p1cta")}</a>
            </div>
            <div className="ra-plan ra-plan--hot">
              <span className="ra-plan-tag">{t("landing.s8.p2tag")}</span>
              <span className="ra-p-k">{t("landing.s8.p2k")}</span>
              <h3>{t("landing.s8.p2t")}</h3>
              <div className="ra-who">{t("landing.s8.p2who")}</div>
              <div className="ra-pr"><b>{pesos(PRECIO_FULL)}</b><span>{t("landing.s8.mes")}</span></div>
              <div className="ra-prn">{t("landing.s8.p2note")}</div>
              <ul>
                {["p2f1", "p2f2", "p2f3", "p2f4", "p2f5", "p2f6"].map((k) => (
                  <li key={k}><i>✓</i><span>{t(`landing.s8.${k}`)}</span></li>
                ))}
              </ul>
              <a className="ra-btn ra-btn--am" href="#contacto">{t("landing.s8.p2cta")}</a>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ 09 · CÓMO EMPEZÁS ══════════ */}
      <section className="ra-sec ra-sec--crema ra-sec--tight" id="empezar">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>09</b> {t("landing.s9.kicker")}</span>
            <h2>{t("landing.s9.h2")}</h2>
            <p className="ra-lead">{t("landing.s9.lead")}</p>
          </div>
          <div className="ra-steps">
            {PASOS.map((k, i) => (
              <div className="ra-step ra-rev" key={k}>
                <div className="ra-step-n">{t("landing.s9.step")} {String(i + 1).padStart(2, "0")}</div>
                <h3>{t(`landing.s9.${k}t`)}</h3>
                <p>{t(`landing.s9.${k}d`)}</p>
                <span className="ra-step-when">{t(`landing.s9.${k}w`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ 10 · LAS DUDAS ══════════ */}
      <section className="ra-sec" id="dudas">
        <div className="ra-wrap">
          <div className="ra-col-9 ra-rev">
            <span className="ra-kick"><b>10</b> {t("landing.s10.kicker")}</span>
            <h2>{t("landing.s10.h2")}</h2>
          </div>
          <div className="ra-faq">
            {DUDAS.map((n) => (
              <div className="ra-qa ra-rev" key={n}>
                <h3>{t(`landing.s10.q${n}`)}</h3>
                <p><Rich text={t(`landing.s10.a${n}`)} /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ TOTAL ══════════ */}
      <section className="ra-total" id="contacto">
        <div className="ra-wrap ra-total-in">
          <div>
            <span className="ra-kick"><b>Total</b> {t("landing.cta.kicker")}</span>
            <h2>
              {t("landing.cta.h2a")} <span className="ra-mk">{t("landing.cta.h2b")}</span> {t("landing.cta.h2c")}
            </h2>
            <p className="ra-lead">{t("landing.cta.lead")}</p>
            <div className="ra-total-cta">
              <a className="ra-btn ra-btn--am" href={waHref} onClick={onWhatsApp} target="_blank" rel="noopener noreferrer">
                <IconoWhatsapp />
                {t("landing.cta.wa")}
              </a>
              <a className="ra-btn ra-btn--lineLt" href={`mailto:${MAIL}`}>{t("landing.cta.mail")}</a>
            </div>
            <div className="ra-total-fine">{t("landing.cta.fine")}</div>

            <form className="ra-form" onSubmit={enviar}>
              <div className="ra-form-t">{t("landing.cta.formTitle")}</div>
              <div className="ra-form-grid">
                <input name="name" value={form.name} onChange={onInput} placeholder={t("landing.cta.formName")} autoComplete="name" />
                <input name="business" value={form.business} onChange={onInput} placeholder={t("landing.cta.formBiz")} autoComplete="organization" />
                <input name="phone" type="tel" value={form.phone} onChange={onInput} placeholder={t("landing.cta.formTel")} autoComplete="tel" />
              </div>
              <button type="submit" className="ra-btn ra-btn--am" disabled={!completo || envio.enviando}>
                {envio.enviando ? t("landing.cta.formSending") : t("landing.cta.formSend")}
              </button>
              <div
                className={`ra-form-msg${envio.ok ? " ra-form-msg--ok" : ""}${envio.error ? " ra-form-msg--err" : ""}`}
                role="status"
                aria-live="polite"
              >
                {envio.ok ? t("landing.cta.formOk") : envio.error ? t("landing.cta.formErr") : ""}
              </div>
            </form>
          </div>

          <div className="ra-receipt">
            <div className="ra-rc-row"><span>{t("landing.rc.carta")}</span><b>{t("landing.rc.incluida")}</b></div>
            <div className="ra-rc-row"><span>{t("landing.rc.reservas")}</span><b>{t("landing.rc.incluidas")}</b></div>
            <div className="ra-rc-row"><span>{t("landing.rc.costos")}</span><b>{t("landing.rc.incluidos")}</b></div>
            <div className="ra-rc-row"><span>{t("landing.rc.pdf")}</span><b>{t("landing.rc.incluido")}</b></div>
            <div className="ra-rc-row"><span>{t("landing.rc.pack")}</span><b>{t("landing.rc.packVal")}</b></div>
            <div className="ra-rc-rule" />
            <div className="ra-rc-row"><span>{t("landing.rc.carga")}</span><b>{t("landing.rc.cargaVal")}</b></div>
            <div className="ra-rc-row"><span>{t("landing.rc.instal")}</span><b>$0</b></div>
            <div className="ra-rc-row"><span>{t("landing.rc.perm")}</span><b>{t("landing.rc.permVal")}</b></div>
            <div className="ra-rc-rule" />
            <div className="ra-rc-tot">
              <span>{t("landing.rc.desde")}</span>
              <b>{pesos(PRECIO_CARTA)}/{t("landing.s8.mes").replace("/ ", "")}</b>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="ra-foot">
        <div className="ra-wrap ra-foot-in">
          <div>
            <a className="ra-foot-brand" href="#top">
              <img src="/images/logoRA.png" alt="" />
              <b>RestaurAdmin</b>
            </a>
            <p>{t("landing.foot.desc")}</p>
          </div>
          <div className="ra-foot-col">
            <h3>{t("landing.foot.producto")}</h3>
            <a href="#sistema">{t("landing.foot.modulos")}</a>
            <a href="#cuenta">{t("landing.foot.cuenta")}</a>
            <a href="#precios">{t("landing.foot.precios")}</a>
            <a href="#empezar">{t("landing.foot.empezar")}</a>
          </div>
          <div className="ra-foot-col">
            <h3>{t("landing.foot.contacto")}</h3>
            <a href={waHref} onClick={onWhatsApp} target="_blank" rel="noopener noreferrer">WhatsApp {WA_VISIBLE}</a>
            <a href={`mailto:${MAIL}`}>{MAIL}</a>
            <a href="https://www.instagram.com/restauradmin/" target="_blank" rel="noopener noreferrer">Instagram</a>
          </div>
          <div className="ra-foot-col">
            <h3>{t("landing.foot.legal")}</h3>
            <a href="/privacy-policy.html">{t("landing.foot.priv")}</a>
            <a href="/terms-of-service.html">{t("landing.foot.terms")}</a>
            <a href="/data-deletion.html">{t("landing.foot.data")}</a>
          </div>
        </div>
        <div className="ra-wrap">
          <div className="ra-foot-bot">
            <span>© {new Date().getFullYear()} RestaurAdmin</span>
            <span>{t("landing.foot.iva")}</span>
          </div>
        </div>
      </footer>

      {/* ══════════ DOCK (celular) ══════════ */}
      <div className="ra-dock">
        <div className="ra-dock-txt">
          <b>{t("landing.dock.desde", { precio: pesos(PRECIO_CARTA) })}</b>
          <span>{t("landing.dock.sin")}</span>
        </div>
        <a className="ra-dock-btn" href={waHref} onClick={onWhatsApp} target="_blank" rel="noopener noreferrer">
          <IconoWhatsapp size={17} />
          {t("landing.dock.cta")}
        </a>
      </div>
    </div>
  );
};

export default Home;
